import React, { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Radar, KeyRound, ArrowLeft, CheckCircle2, ArrowRight } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import PasswordStrength, { scorePassword } from "../components/PasswordStrength";
import PasswordInput from "../components/PasswordInput";
import api from "../lib/api";
import { useToast } from "../hooks/use-toast";

const ResetPassword = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { toast } = useToast();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast({ title: "Password too short", description: "Use at least 8 characters.", variant: "destructive" }); return; }
    if (pw !== confirm) { toast({ title: "Passwords don't match", variant: "destructive" }); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      toast({ title: "Reset failed", description: err?.response?.data?.detail || "The link may be invalid or expired.", variant: "destructive" });
    } finally { setBusy(false); }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50/60 px-6 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-8 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800"><ArrowLeft className="h-4 w-4" /> Back to home</Link>
        <div className="rounded-2xl border border-slate-100 bg-white p-8 shadow-sm">
          {!done ? (
            <>
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50"><KeyRound className="h-7 w-7 text-indigo-700" /></div>
              <h1 className="text-2xl font-bold text-[#1a1a2e]">Choose a new password</h1>
              <p className="mt-1 text-sm text-slate-500">Your new password must be at least 8 characters.</p>
              {!token && <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">Missing reset token. Please use the link from your email.</p>}
              <form onSubmit={submit} className="mt-6 space-y-3">
                <div className="space-y-1.5">
                  <Label>New password</Label>
                  <PasswordInput required value={pw} onChange={(e) => setPw(e.target.value)} placeholder="••••••••" data-testid="reset-new-password" />
                  <PasswordStrength password={pw} />
                </div>
                <div className="space-y-1.5">
                  <Label>Confirm password</Label>
                  <PasswordInput required value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" data-testid="reset-confirm-password" />
                </div>
                <Button type="submit" disabled={busy || !token} className="group w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">
                  {busy ? "Updating..." : "Update password"}
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                </Button>
              </form>
            </>
          ) : (
            <div className="py-4 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
              <h3 className="text-xl font-bold text-[#1a1a2e]">Password updated</h3>
              <p className="mt-2 text-sm text-slate-500">You can now sign in with your new password.</p>
              <Button onClick={() => navigate("/auth/admin")} className="mt-6 w-full rounded-xl bg-[#2f2b8f] py-6 text-sm font-semibold text-white hover:bg-[#26236f]">Go to sign in</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
